import React from 'react';
import { FiUsers, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import {
  FormSection,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  Select,
  CheckboxGroup,
  CheckboxLabel,
  InfoBox,
  HelpText,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton
} from '../common/FormStyles';
import { InformalClients, ClientReference } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { INFORMAL_CLIENTS_DOCUMENTS } from '@/config/documentsBySection';
import { Candidate } from '@/models';

interface InformalClientsSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
   onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
}

export const InformalClientsSection: React.FC<InformalClientsSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleClientTypeToggle = (type: string, checked: boolean) => {
    const currentTypes = data.types_clients || [];
    const newTypes = checked
      ? [...currentTypes, type as any]
      : currentTypes.filter(t => t !== type);
    handleChange('types_clients', newTypes);
  };

  const handleClientChange = (index: number, field: string, value: any) => {
    const newClients = [...(data.clients_references || [])];
    newClients[index] = { ...newClients[index], [field]: value };
    handleChange('clients_references', newClients);
  };

  const addClient = () => {
    const newClient: ClientReference = {
      id: generateId(),
      nom: '',
      telephone: '',
      client_depuis: 'Moins de 6 mois' as any,
      accepte_contact: false
    };
    handleChange('clients_references', [...(data.clients_references || []), newClient]);
  };

  const removeClient = (index: number) => {
    const newClients = (data.clients_references || []).filter((_, i) => i !== index);
    handleChange('clients_references', newClients);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiUsers />
        Vos Clients
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Les informations sur vos clients nous aident à vérifier la réalité de votre activité.
          Plus vous aurez de clients pouvant témoigner, plus votre dossier sera solide.
        </p>
      </InfoBox>

      <FormGroup>
        <Label $required>Qui sont vos clients principaux ?</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Habitants du quartier')}
              onChange={e => handleClientTypeToggle('Habitants du quartier', e.target.checked)}
              disabled={readonly}
            />
            Habitants du quartier
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Passants/Grand public')}
              onChange={e => handleClientTypeToggle('Passants/Grand public', e.target.checked)}
              disabled={readonly}
            />
            Passants / Grand public
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Autres commerçants')}
              onChange={e => handleClientTypeToggle('Autres commerçants', e.target.checked)}
              disabled={readonly}
            />
            Autres commerçants
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Clients réguliers/fidèles')}
              onChange={e => handleClientTypeToggle('Clients réguliers/fidèles', e.target.checked)}
              disabled={readonly}
            />
            Clients réguliers / fidèles
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Entreprises')}
              onChange={e => handleClientTypeToggle('Entreprises', e.target.checked)}
              disabled={readonly}
            />
            Entreprises
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Autre')}
              onChange={e => handleClientTypeToggle('Autre', e.target.checked)}
              disabled={readonly}
            />
            Autre
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs types</HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Nombre approximatif de clients réguliers</Label>
        <Input
          type="number"
          min="0"
          value={data.nombre_clients_reguliers || ''}
          onChange={e => handleChange('nombre_clients_reguliers', parseInt(e.target.value))}
          placeholder="Ex: 20"
          disabled={readonly}
        />
        <HelpText>
          Clients qui reviennent régulièrement acheter chez vous ou utiliser vos services
        </HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Clients qui peuvent témoigner pour vous</Label>
        <InfoBox $type="warning">
          <p>
            <strong>Important :</strong> Ajoutez au moins 3 clients qui peuvent confirmer que vous
            travaillez régulièrement. Ces personnes pourront être contactées pour vérification.
          </p>
        </InfoBox>
      </FormGroup>

      {(data.clients_references || []).map((client, index) => (
        <DynamicEntryContainer key={client.id}>
          <EntryHeader>
            <EntryTitle>
              <EntryNumber>{index + 1}</EntryNumber>
              Client référence {index + 1}
            </EntryTitle>
            <RemoveButton
              onClick={() => removeClient(index)}
              disabled={readonly}
            >
              <FiTrash2 />
              Supprimer
            </RemoveButton>
          </EntryHeader>

          <FormGroup>
            <Label $required>Nom complet du client</Label>
            <Input
              type="text"
              value={client.nom || ''}
              onChange={e => handleClientChange(index, 'nom', e.target.value)}
              placeholder="Nom et prénoms"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label $required>Numéro de téléphone</Label>
            <Input
              type="tel"
              value={client.telephone || ''}
              onChange={e => handleClientChange(index, 'telephone', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label $required>Client depuis combien de temps ?</Label>
            <Select
              value={client.client_depuis || ''}
              onChange={e => handleClientChange(index, 'client_depuis', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Moins de 6 mois">Moins de 6 mois</option>
              <option value="6-12 mois">6 à 12 mois</option>
              <option value="1-2 ans">1 à 2 ans</option>
              <option value="2-5 ans">2 à 5 ans</option>
              <option value="Plus de 5 ans">Plus de 5 ans</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={client.accepte_contact || false}
                onChange={e => handleClientChange(index, 'accepte_contact', e.target.checked)}
                disabled={readonly}
              />
              <strong>Ce client accepte d'être contacté pour témoigner</strong>
            </CheckboxLabel>
            <HelpText>
              Assurez-vous d'avoir informé ce client qu'il pourrait être contacté
            </HelpText>
          </FormGroup>
        </DynamicEntryContainer>
      ))}

      {!readonly && (
        <AddButton onClick={addClient}>
          <FiPlus />
          Ajouter un client référence
        </AddButton>
      )}

      {(data.clients_references || []).length < 3 && (
        <InfoBox $type="warning">
          <p>
            Vous avez ajouté {(data.clients_references || []).length} client(s).
            Nous recommandons d'en ajouter au moins 3 pour renforcer votre dossier.
          </p>
        </InfoBox>
      )}

      {(data.clients_references || []).length >= 3 && (
        <InfoBox $type="info">
          <p>
            <strong>Excellent !</strong> Vous avez {(data.clients_references || []).length} client(s)
            référence(s). Cela renforce votre dossier.
          </p>
        </InfoBox>
      )}




      {onDocumentUpload && onDocumentRemove && (
                        <DocumentUpload
                          sectionTitle="Vos Clients"
                          documents={INFORMAL_CLIENTS_DOCUMENTS}
                          uploadedFiles={uploadedDocuments}
                          onFileSelect={onDocumentUpload}
                          onFileRemove={onDocumentRemove}
                          readonly={readonly}
                        />
                      )}
    </FormSection>
  );
};
